import { createContext, useCallback, useEffect, useState } from 'react';
import { useAuth as useClerkAuth, useClerk, useUser } from '@clerk/react';
import i18n from 'i18next';
import { registerGetToken, clearToken } from '../utils/tokenManager';
import { obterUsuarioMe } from '../api/usuarios';
import { clearImpersonado } from '../utils/impersonation';
import { statusErro } from '../utils/apiError';

/**
 * Sessão do usuário. A autenticação é do Clerk; aqui juntamos o usuário do Clerk
 * com o que vem de GET /usuarios/me da admin-api (tenants, role global, nome e
 * idioma preferido). O token não é guardado: os interceptors pedem um fresco ao
 * Clerk a cada request (utils/tokenManager).
 */

const ESTADO_INICIAL = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  tenants: [],
  needsRegistration: false,
  roleGlobal: null,
};

const DESLOGADO = { ...ESTADO_INICIAL, isInitialized: true };

const AuthContext = createContext(null);

// Nome da admin-api tem prioridade: no cadastro de produtor/jurado o nome real vai
// pra lá, e o Clerk (signup por convite pede só e-mail e senha) pode não ter nome.
function nomePreferido(nomeAdminApi, usuario) {
  return nomeAdminApi && nomeAdminApi.trim() && nomeAdminApi !== usuario.email ? nomeAdminApi : usuario.nome;
}

function AuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();
  const { user: clerkUser } = useUser();
  const [state, setState] = useState(ESTADO_INICIAL);

  useEffect(() => {
    if (!isLoaded) return;

    const initialize = async () => {
      if (!isSignedIn) {
        clearToken();
        setState(DESLOGADO);
        return;
      }

      try {
        // Interceptors pedem token fresco ao Clerk a cada request (template api-central).
        registerGetToken(() => getToken({ template: 'api-central' }).catch(() => getToken()));

        const usuario = {
          id: clerkUser?.id,
          clerkUserId: clerkUser?.id,
          nome:
            clerkUser?.fullName ||
            [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ') ||
            clerkUser?.username ||
            clerkUser?.primaryEmailAddress?.emailAddress,
          email: clerkUser?.primaryEmailAddress?.emailAddress,
          imageUrl: clerkUser?.imageUrl,
        };

        let tenants = [];
        let needsRegistration = false;
        let roleGlobal = null;
        try {
          const response = await obterUsuarioMe();
          const data = response.data?.data || response.data;
          tenants = Array.isArray(data) ? data : (data?.tenants ?? []);
          roleGlobal = data?.usuario?.role ?? null;
          usuario.nome = nomePreferido(data?.usuario?.nome, usuario);
          // Idioma preferido do usuário (ex.: 'en-US' → 'en')
          const idiomaPreferido = data?.usuario?.idiomaPreferido;
          if (idiomaPreferido) {
            const lang = idiomaPreferido.split('-')[0].toLowerCase();
            window.localStorage.setItem('konq.idioma', lang);
            i18n.changeLanguage(lang);
          }
        } catch (err) {
          // 404 em /me: usuário existe no Clerk mas ainda não tem cadastro na plataforma
          if (statusErro(err) === 404) needsRegistration = true;
        }

        setState({
          isAuthenticated: true,
          isInitialized: true,
          user: usuario,
          tenants,
          roleGlobal,
          needsRegistration,
        });
      } catch (err) {
        console.error('Initialization error:', err);
        setState(DESLOGADO);
      }
    };

    initialize();
  }, [isLoaded, isSignedIn, getToken]);

  const signOut = async () => {
    localStorage.removeItem('slug');
    // Limpa impersonation pra não vazar pro próximo login no mesmo navegador.
    clearImpersonado();
    clearToken();
    setState(DESLOGADO);
    await clerkSignOut();
  };

  // Recarrega /me (tenants, role global, nome) — ex.: depois do cadastro no wizard.
  const refreshUser = useCallback(async () => {
    try {
      const response = await obterUsuarioMe();
      const data = response.data?.data || response.data;
      setState((atual) => ({
        ...atual,
        isAuthenticated: true,
        needsRegistration: false,
        tenants: Array.isArray(data) ? data : (data?.tenants ?? []),
        roleGlobal: data?.usuario?.role ?? null,
        user: atual.user ? { ...atual.user, nome: nomePreferido(data?.usuario?.nome, atual.user) } : atual.user,
      }));
    } catch {
      // silencioso — não interrompe a navegação se /me falhar
    }
  }, []);

  // Foto vem reativa do Clerk (pode carregar depois da inicialização).
  const user = state.user ? { ...state.user, imageUrl: clerkUser?.imageUrl ?? state.user.imageUrl } : null;

  return <AuthContext.Provider value={{ ...state, user, signOut, refreshUser }}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
